<script lang="ts">
	// UC-38. The draft bar of Menu and Discounts (D8, D9, D21) and the Publish
	// sheet (D24), as the Owner Dashboard - Menu page draws them and the
	// Patterns board's menu draft says.
	//
	// The bar says what the tills sell: unpublished changes on an ink Band
	// with Publish menu, a menu waiting for its time as a quiet row with
	// Cancel, or that the tills have this menu. Sold out is never in the
	// draft: it reaches the tills at once.
	//
	// Publish reads the draft afresh and lists what changes against the
	// latest menu, the prices far from the ones they replace with one Confirm
	// for the list, what the owner must know, and when it takes effect: now,
	// or a day and a time in the restaurant's timezone. A refusal reads the
	// draft again and says what changed. Cancelling the waiting menu is
	// undone, not confirmed: the server hears of it when the Undo runs out.

	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Choice from '../components/Choice.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import Segmented from '../components/Segmented.svelte';
	import Sheet from '../components/Sheet.svelte';
	import { fill, message, type Language } from '../i18n/messages';
	import type { Account } from '../owner/session.svelte';
	import { ownerShops } from '../owner/data.svelte';
	import { commitNow, removeWithUndo } from '../owner/notices.svelte';
	import {
		cancelWaiting,
		cancellingVersion,
		changeGroups,
		changesPhrase,
		confirmations,
		flagLine,
		lineCount,
		loadDraft,
		minutesPhrase,
		ownerDraft,
		publish,
		setCancellingVersion,
		todayIn,
		whenPhrase,
		timezoneCity,
		type Wording,
	} from '../owner/draft.svelte';

	interface Props {
		language: Language;
		account: Account;
	}

	let { language, account }: Props = $props();

	const draft = $derived(ownerDraft());
	const shops = $derived(ownerShops());

	$effect(() => {
		if (!draft.loaded) void loadDraft(message(language, 'loadFailed'));
	});

	const wording: Wording = $derived({
		language,
		minorDigits: account.settings.minorDigits,
		shopName: (id: string) => shops.value.find((s) => s.id === id)?.name ?? id,
	});

	// The waiting menu the owner just cancelled leaves the bar at once; the
	// server hears of it when the Undo runs out (the store holds which one).
	let barFailure = $state('');

	const value = $derived(draft.value);
	const waiting = $derived(
		value?.waiting && value.waiting.id !== cancellingVersion() ? value.waiting : null,
	);
	const groups = $derived(value ? changeGroups(value.changes ?? [], wording) : []);
	const count = $derived(lineCount(groups));
	const flagged = $derived(value?.flagged ?? []);

	function cancel() {
		const target = waiting;
		if (!target) return;
		barFailure = '';
		if (refusal === 'scheduled') refusal = null;
		setCancellingVersion(target.id);
		removeWithUndo(
			message(language, 'draftCancelled'),
			async () => {
				const done = await cancelWaiting(target.id, message(language, 'loadFailed'));
				if (!done.ok) {
					barFailure =
						done.code === 'NOTHING_SCHEDULED' ? message(language, 'draftTooLate') : done.message;
				}
				await loadDraft(message(language, 'loadFailed'));
				setCancellingVersion('');
			},
			() => setCancellingVersion(''),
		);
	}

	// ---------- the Publish sheet ----------

	type Refusal = 'scheduled' | 'changed' | 'nothing' | 'past' | 'unconfirmed' | 'failed';

	let open = $state(false);
	let reading = $state(false);
	let busy = $state(false);
	let confirmed = $state(false);
	let refusal = $state<Refusal | null>(null);
	let failure = $state('');
	let when = $state<'now' | 'later'>('now');
	let day = $state('');
	let time = $state('');

	async function openPublish() {
		open = true;
		reading = true;
		confirmed = false;
		failure = '';
		when = 'now';
		day = '';
		time = '';
		// A removal still waiting for its Undo goes to the server first, so
		// the draft shown is the draft published.
		await commitNow();
		await loadDraft(message(language, 'loadFailed'));
		refusal = waiting ? 'scheduled' : null;
		reading = false;
	}

	function clear(next?: () => void) {
		next?.();
		if (refusal !== 'scheduled') refusal = null;
	}

	async function go() {
		if (!value || busy) return;
		busy = true;
		failure = '';
		// What the owner was shown and confirmed, taken before anything reads
		// the draft again: the server refuses the publish when the draft has
		// changed since (DRAFT_CHANGED), or when it flags a price this list
		// does not confirm (PRICE_CHANGE_UNCONFIRMED).
		const shown = value.draft;
		const confirmedPrices = confirmed ? confirmations(flagged) : [];
		try {
			// A cancel of the waiting menu still in its Undo is sent first.
			await commitNow();
			const done = await publish(
				shown,
				when === 'later' ? { date: day, time } : null,
				confirmedPrices,
				message(language, 'publishFailed'),
			);
			if (done.ok) {
				open = false;
				refusal = null;
				barFailure = '';
				await loadDraft(message(language, 'loadFailed'));
				return;
			}
			switch (done.code) {
				case 'VERSION_SCHEDULED':
					refusal = 'scheduled';
					break;
				case 'DRAFT_CHANGED':
					refusal = 'changed';
					confirmed = false;
					break;
				case 'NOTHING_TO_PUBLISH':
					refusal = 'nothing';
					break;
				case 'EFFECTIVE_TIME_PAST':
					refusal = 'past';
					return;
				case 'PRICE_CHANGE_UNCONFIRMED':
					refusal = 'unconfirmed';
					confirmed = false;
					break;
				default:
					refusal = 'failed';
					failure = done.code === '' ? message(language, 'publishFailed') : done.message;
					return;
			}
			await loadDraft(message(language, 'loadFailed'));
		} finally {
			busy = false;
		}
	}

	const band = $derived.by(() => {
		switch (refusal) {
			case 'scheduled':
				return {
					text: message(language, 'publishScheduled'),
					action: waiting ? message(language, 'publishCancelIt') : undefined,
					onAction: cancel,
					testId: 'publish-scheduled',
				};
			case 'changed':
				return { text: message(language, 'publishChanged'), testId: 'publish-changed' };
			case 'nothing':
				return { text: message(language, 'publishNothing'), testId: 'publish-nothing' };
			case 'failed':
				return {
					text: failure || message(language, 'publishFailed'),
					action: message(language, 'retry'),
					onAction: () => void go(),
					testId: 'publish-failed',
				};
			default:
				return null;
		}
	});

	const waitingGroups = $derived(
		value?.waiting ? changeGroups(value.waiting.changes ?? [], wording) : [],
	);

	const publishOff = $derived(
		reading ||
			!value ||
			!!waiting ||
			count === 0 ||
			(flagged.length > 0 && !confirmed) ||
			(when === 'later' && (!day || !time)) ||
			busy,
	);

	// The earliest day the day field offers: today, in the restaurant's
	// timezone. The server refuses a time already past either way.
	const today = $derived(todayIn(account.settings.timezone));
</script>

{#if draft.loaded && !value && draft.failure}
	<!-- The draft could not be read: say so, with Retry, rather than hide the bar. -->
	<Band
		tone="flag"
		alert
		action={message(language, 'retry')}
		onAction={() => void loadDraft(message(language, 'loadFailed'))}
		actionTestId="draft-retry"
		testId="draft-unread"
	>
		{draft.failure}
	</Band>
{/if}

{#if draft.loaded && value}
	<div class="bar" data-testid="draft-bar">
		{#if waiting}
			<div class="row" data-testid="draft-waiting">
				<p class="strong">
					{fill(
						message(language, 'draftWaiting'),
						whenPhrase(language, waiting.effectiveDate, waiting.effectiveTime),
					)}
				</p>
				<Button
					testId="draft-cancel"
					label={message(language, 'cancel')}
					tone="secondary"
					size="sm"
					onPress={cancel}
				/>
			</div>
		{/if}
		{#if count > 0}
			<Band
				tone="ink"
				action={message(language, 'draftPublish')}
				onAction={() => void openPublish()}
				actionTestId="draft-publish"
				testId="draft-changes"
			>
				{changesPhrase(language, count)}
			</Band>
		{:else if !waiting}
			<div class="row" data-testid="draft-clean">
				<p class="quiet">{message(language, 'draftClean')}</p>
			</div>
		{/if}
		{#if barFailure || draft.failure}
			<p class="failure" role="alert" data-testid="draft-failure">{barFailure || draft.failure}</p>
		{/if}
	</div>
{/if}

<Sheet
	{open}
	title={message(language, 'draftPublish')}
	onClose={() => (open = false)}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="publish-close"
	primaryLabel={message(language, 'confirm')}
	primaryIcon="check"
	onPrimary={() => void go()}
	primaryDisabled={publishOff}
	primaryState={busy ? 'pending' : 'idle'}
	pendingLabel={message(language, 'publishPending')}
	primaryTestId="publish-confirm"
	testId="publish-sheet"
>
	<div class="sheet" data-scroll="">
		{#if reading || !value}
			<div class="wait">
				<Loading
					size="lg"
					label={message(language, 'publishLoading')}
					caption={message(language, 'publishLoading')}
				/>
			</div>
		{:else}
			{#if band}
				<Band
					tone="flag"
					alert
					action={band.action}
					onAction={band.onAction}
					actionTestId="{band.testId}-action"
					testId={band.testId}
				>
					{band.text}
				</Band>
			{/if}
			<p class="quiet" data-testid="publish-in-effect">
				{fill(
					message(language, 'publishInEffect'),
					value.inEffect.number,
					whenPhrase(language, value.inEffect.effectiveDate, value.inEffect.effectiveTime),
				)}
			</p>
			{#if value.waiting && refusal === 'scheduled'}
				<div class="block" data-testid="publish-waiting">
					<h3>
						{fill(
							message(language, 'publishWaitingTitle'),
							value.waiting.number,
							whenPhrase(language, value.waiting.effectiveDate, value.waiting.effectiveTime),
						)}
					</h3>
					{#each waitingGroups as group (group.key)}
						<div class="group">
							<span class="title">{group.title}</span>
							{#each group.lines as line, index (index)}
								<p class="line">
									<span>{line.lead}</span>
									{#if line.move}<bdi dir="ltr" class="move">{line.move}</bdi>{/if}
								</p>
							{/each}
						</div>
					{/each}
				</div>
			{/if}
			{#if count > 0}
				<div class="block" data-testid="publish-changes">
					<div class="head">
						<h3>{message(language, 'publishChangesTitle')}</h3>
						<span class="quiet">{changesPhrase(language, count)}</span>
					</div>
					{#each groups as group (group.key)}
						<div class="group">
							<span class="title">{group.title}</span>
							{#each group.lines as line, index (index)}
								<p class="line" data-testid="publish-line">
									<span>{line.lead}</span>
									{#if line.move}<bdi dir="ltr" class="move">{line.move}</bdi>{/if}
								</p>
							{/each}
						</div>
					{/each}
				</div>
			{/if}
			{#if flagged.length > 0}
				<div class="flags" data-testid="publish-flags">
					<h3>{message(language, 'priceCheckTitle')}</h3>
					<p class="flag-text">{message(language, 'priceCheckText')}</p>
					{#each flagged as flag (`${flag.itemId}-${flag.shopId ?? ''}`)}
						{@const line = flagLine(flag, wording)}
						<p class="line flag-line">
							<span>{line.lead}</span>
							<bdi dir="ltr" class="move">{line.move}</bdi>
						</p>
					{/each}
					<Choice
						mode="multi"
						testId="publish-prices-right"
						options={[
							{
								value: 'right',
								label: message(language, 'publishPricesRight'),
								testId: 'publish-prices-right-option',
							},
						]}
						value={confirmed ? ['right'] : []}
						onChange={(picked) => clear(() => (confirmed = (picked as string[]).includes('right')))}
						stack
					/>
				</div>
			{/if}
			<div class="block">
				<h3>{message(language, 'publishBeforeTitle')}</h3>
				<ul>
					<li>
						{fill(
							message(language, 'publishTills'),
							minutesPhrase(language, value.tillCheckSeconds),
						)}
					</li>
					<li>
						{fill(
							message(language, 'publishWindow'),
							minutesPhrase(language, value.priceWindowSeconds),
						)}
					</li>
					<li>{message(language, 'publishSoldOut')}</li>
				</ul>
			</div>
			<div class="block">
				<h3>{message(language, 'publishWhenTitle')}</h3>
				<Segmented
					label={message(language, 'publishWhenTitle')}
					testId="publish-when"
					options={[
						{ value: 'now', label: message(language, 'publishNow'), testId: 'publish-now' },
						{ value: 'later', label: message(language, 'publishLater'), testId: 'publish-later' },
					]}
					value={when}
					onChange={(picked) => clear(() => (when = picked as 'now' | 'later'))}
				/>
				{#if when === 'later'}
					<div class="when">
						<Field
							testId="publish-day"
							label={message(language, 'publishDay')}
							type="date"
							mono
							min={today}
							value={day}
							onInput={(v) => clear(() => (day = v))}
						/>
						<Field
							testId="publish-time"
							label={message(language, 'publishTime')}
							type="time"
							mono
							value={time}
							error={refusal === 'past' ? message(language, 'publishPast') : undefined}
							errorTestId="publish-past"
							onInput={(v) => clear(() => (time = v))}
						/>
					</div>
					<p class="local-time">
						{fill(
							message(language, 'publishTimezone'),
							timezoneCity(language, account.settings.timezone),
						)}
					</p>
				{/if}
			</div>
		{/if}
	</div>
</Sheet>

<style>
	.bar {
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-3) var(--space-5);
		min-block-size: var(--ctl-min);
		padding: var(--space-4) var(--space-5);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-panel);
	}

	.row p {
		margin: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
	}

	.strong {
		flex: 1 1 260px;
		font-weight: 600;
		color: var(--text-strong);
	}

	.quiet {
		margin: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.failure {
		margin: 0;
		font-size: var(--text-2xs);
		font-weight: 600;
		line-height: var(--leading-snug);
		color: var(--ink-flag-deep);
	}

	.sheet {
		flex: 1;
		min-block-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
		max-inline-size: var(--measure);
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 200px;
	}

	.block {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.head {
		display: flex;
		flex-wrap: wrap;
		align-items: baseline;
		gap: var(--space-2) var(--space-4);
	}

	h3 {
		margin: 0;
		font-size: var(--text-md);
		font-weight: 700;
		line-height: var(--leading-snug);
		color: var(--text-strong);
	}

	.group {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
	}

	.title {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.line {
		margin: 0;
		display: flex;
		flex-wrap: wrap;
		gap: 0 var(--space-2);
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-strong);
	}

	.move {
		font-family: var(--font-mono);
		font-weight: 600;
	}

	.flags {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
		padding: var(--space-7);
		background: var(--surface-flag);
		border: var(--border-width-strong) solid var(--border-flag);
		border-radius: var(--radius-card);
	}

	.flags h3,
	.flag-text,
	.flag-line {
		color: var(--ink-flag-deep);
	}

	.flag-text {
		margin: 0;
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
	}

	.flag-line {
		font-weight: 600;
	}

	ul {
		margin: 0;
		padding-inline-start: 1.2em;
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.when {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
		gap: var(--space-5);
		align-items: start;
	}

	.local-time {
		margin: 0;
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}
</style>
